import {
  cn,
  evaluate,
  TokenIcon,
  TokenIconSymbol,
  Typography,
  TypographySize,
} from "@kyan/ui";
import type { HTMLAttributes } from "react";

import { UnderlyingSymbol } from "#/types/markets";
import { formatCompact, formatValue } from "#/utils/valueFormatters";

export const SIZE_DISPLAY_SIZES = [
  8, 10, 12, 14, 16,
] as const satisfies readonly TypographySize[];

export type SizeDisplaySize = (typeof SIZE_DISPLAY_SIZES)[number];

export interface SizeDisplayProps extends HTMLAttributes<HTMLDivElement> {
  size?: SizeDisplaySize;
  value: number | string | undefined;
  error?: boolean;
  signed?: boolean;
  suffix?: string;
  compact?: boolean;
  template?: string;
  precision?: number;
  underlying?: UnderlyingSymbol;
  iconClassName?: string;
  valueClassName?: string;
}

export const SizeDisplay: React.FC<SizeDisplayProps> = ({
  size = 12,
  value,
  signed,
  error,
  compact = false,
  suffix,
  precision = 2,
  template = "0.00",
  className,
  underlying,
  iconClassName,
  valueClassName,
  ...props
}) => {
  if (typeof value !== "number") {
    return (
      <Typography primary className={valueClassName}>
        {value ?? "-"}
      </Typography>
    );
  }

  const isSigned =
    signed || ["{-}", "{±}"].some((pattern) => template.includes(pattern));
  const iconSize = evaluate({ xs: size <= 12, sm: size > 12 });

  const typographyClassName = cn(
    "leading-none",
    {
      "text-success": isSigned && value > 0 && !error,
      "text-error": (isSigned && value < 0) || error,
    },
    valueClassName,
  );

  return (
    <div className={cn("flex items-center gap-1", className)} {...props}>
      {underlying && (
        <TokenIcon
          size={iconSize}
          symbol={underlying.toLowerCase() as TokenIconSymbol}
          className={cn(
            "text-textSecondary aspect-auto size-auto",
            iconClassName,
          )}
        />
      )}
      <Typography primary size={size} className={typographyClassName}>
        {compact
          ? formatCompact(value)
          : formatValue(value, {
              template: precision ? undefined : template,
              precision,
            })}
      </Typography>
      {suffix && (
        <Typography className={typographyClassName}>{suffix}</Typography>
      )}
    </div>
  );
};
